import { ApolloClient, ApolloProvider } from '@apollo/client'
import merge from 'lodash/merge'
import { AppProps } from 'next/app'
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { createClient } from 'shared/services/apollo/client'
import logger from 'shared/services/logger'
import {
  hasCompleteUserData,
  mergeSessionData,
} from 'shared/utils/auth/session-merge-utils'
import { isUserError } from 'shared/utils/errors'

import { getClientSession } from './../client-session'
import { Context } from './../client-session-context'
import { ClientSession } from './../utils'

interface SessionProviderProps {
  session?: ClientSession | null
  children: ReactNode
}

/**
 * Check if a session is valid and not expired
 */
const isSessionValid = (session: ClientSession | null): boolean => {
  if (!session) return false
  return session.expiration > Date.now()
}

/**
 * Check if a session is close to expiring (within buffer time)
 */
const isSessionCloseToExpiring = (
  session: ClientSession | null,
  bufferMs: number = 30000,
): boolean => {
  if (!session) return false
  return session.expiration - Date.now() <= bufferMs
}
/**
 * @deprecated Use SessionProvider from shared/contexts/session-provider/session-provider instead
 */
export const SessionProvider = ({
  children,
  session: defaultSession,
}: SessionProviderProps) => {
  const [session, setSession] = useState<ClientSession | null>(
    defaultSession || null,
  )
  const sessionRef = useRef<ClientSession | null>(defaultSession || null)
  const [isActive, setIsActive] = useState<boolean>(Boolean(defaultSession))
  const [isFailed, setIsFailed] = useState<boolean>(false)
  const updateInProgressRef = useRef<boolean>(false)

  // Keep ref in sync with state so updateSession can read current value without being in deps
  useEffect(() => {
    sessionRef.current = session
  }, [session])

  const shouldLoadSession = useMemo(() => {
    // Check for both null and undefined (null == undefined is true)
    return (
      !isActive &&
      !isFailed &&
      (defaultSession === null || defaultSession === undefined)
    )
  }, [isActive, isFailed, defaultSession])

  const updateSession = useCallback(async () => {
    if (isFailed || updateInProgressRef.current) return

    try {
      updateInProgressRef.current = true
      setIsFailed(false)

      logger.debug(
        `SessionProvider: Starting session update (hasSession: ${!!sessionRef.current})`,
      )

      // getClientSession now has built-in retry via retrySessionFetch in makeSessionRequest
      // Auth errors (UnauthorizedError, ForbiddenError, PermissionDeniedError) fail immediately
      // Transient errors are retried automatically
      const newSession = await getClientSession()

      // Defensive check: getClientSession should never return null (it throws errors)
      // If null is returned, treat as unexpected error and clear session
      if (!newSession) {
        logger.debug(
          'SessionProvider: Unexpected null session (should have thrown error)',
        )
        setSession(null)
        setIsFailed(true)
        updateInProgressRef.current = false
        return
      }

      // Check if new session has expired
      if (!isSessionValid(newSession)) {
        if (!sessionRef.current || !isSessionValid(sessionRef.current)) {
          logger.debug(
            'SessionProvider: Invalid session received and no valid existing session',
          )
          setIsFailed(true)
          setSession(null)
          updateInProgressRef.current = false
          return
        } else {
          logger.debug(
            'SessionProvider: Invalid new session, preserving existing valid session',
          )
          updateInProgressRef.current = false
          return
        }
      }

      // Check if new session has complete user data
      if (!hasCompleteUserData(newSession)) {
        logger.info(
          'SessionProvider: New session missing critical user data, attempting merge: ' +
            JSON.stringify({
              hasUserId: newSession.user?.id || 'missing',
              hasUserRole: newSession.user?.role || 'missing',
              hasOrgId: newSession.user?.organization?.id || 'missing',
            }),
        )

        // If we have an existing complete session, merge to preserve good data
        if (sessionRef.current && hasCompleteUserData(sessionRef.current)) {
          const mergedSession = mergeSessionData(sessionRef.current, newSession)
          setSession(mergedSession)
          updateInProgressRef.current = false
          return
        } else {
          // No existing complete session to fall back to - must accept incomplete
          logger.error(
            new Error(
              'SessionProvider: Incomplete session and no valid existing session to merge',
            ),
            {
              extra: {
                hasExistingSession: !!sessionRef.current,
                existingComplete: hasCompleteUserData(sessionRef.current),
              },
            },
          )
          // Accept incomplete session rather than clearing (server should handle refetch)
          setSession(newSession)
          updateInProgressRef.current = false
          return
        }
      }

      // Even on success, use smart merge to be extra defensive
      const mergedSession = sessionRef.current
        ? mergeSessionData(sessionRef.current, newSession)
        : newSession

      logger.debug(
        `SessionProvider: Successfully updated session (tokenLength: ${mergedSession.accessToken?.length || 0}, timeUntilExpiration: ${mergedSession.expiration - Date.now()}ms)`,
      )

      setSession(mergedSession)
    } catch (error) {
      logger.debug(`SessionProvider: Error updating session: ${error}`)

      // Auth errors (401/403) from /auth/session fail immediately - user must log out
      // These are NOT retried by retrySessionFetch (shouldFailImmediately)
      if (isUserError(error)) {
        logger.debug('SessionProvider: Auth error detected, clearing session')
        setSession(null)
        setIsFailed(true)
        updateInProgressRef.current = false
        return
      }

      // Transient errors (500/network) exhausted retries - preserve existing session
      // These were already retried 2x in /auth/session route + 1x in retrySessionFetch
      // Don't log user out for network glitches
      logger.debug(
        'SessionProvider: Transient error after retries exhausted, preserving existing session',
      )
      setIsFailed(false) // Don't mark as failed since we're preserving session
    } finally {
      updateInProgressRef.current = false
      setIsActive(true)
    }
  }, [isFailed])

  /**
   * Ideally the page is providing the server-session via pageProps
   * to initialize this provider, but it isn't guaranteed so we need
   * to make our own initialization request if no session is seeded
   */
  useEffect(() => {
    if (shouldLoadSession) updateSession()
  }, [shouldLoadSession, updateSession])

  /**
   * Keep the session context active, even when inactive. Will try
   * and refresh the client session as soon as it's expired
   */
  useEffect(() => {
    if (!session) return

    /**
     * Handling already expired sessions. This, ideally, shouldn't ever
     * happen as there's a lot of upstream logic that is generating a
     * "fresh" session on each SSR and then this context just needs to
     * keep it active during idle sessions.
     **/
    const expirationTime = Math.max(session?.expiration - Date.now(), 0)

    const checkSessionExpiration = setTimeout(() => {
      void updateSession()
    }, expirationTime)

    return () => clearTimeout(checkSessionExpiration)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.expiration])

  /**
   * Handle tab visibility changes to ensure session stays fresh
   * when user returns to the tab after Chrome throttled timers
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && session) {
        // Check if session is expired or about to expire when tab becomes visible
        const timeUntilExpiration = session.expiration - Date.now()
        const shouldRefresh = timeUntilExpiration <= 30000 // 30 seconds buffer

        if (shouldRefresh) {
          void updateSession()
        }
      }
    }

    const handleFocus = () => {
      if (session) {
        // Additional check on window focus (user switching back to tab)
        const timeUntilExpiration = session.expiration - Date.now()
        const shouldRefresh = timeUntilExpiration <= 60000 // 1 minute buffer

        if (shouldRefresh) {
          void updateSession()
        }
      }
    }

    const handleOnline = () => {
      // Refresh session when network comes back online
      if (session && isSessionCloseToExpiring(session, 300000)) {
        // 5 minute buffer
        logger.debug('Network restored, refreshing session')
        void updateSession()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleFocus)
    if (process.env.NODE_ENV !== 'test') {
      window.addEventListener('online', handleOnline)
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleFocus)
      if (process.env.NODE_ENV !== 'test') {
        window.removeEventListener('online', handleOnline)
      }
    }
  }, [session, updateSession])

  // Note: Removed sessionStorage updates to eliminate race conditions.
  // GraphQL auth link now always uses getClientSession() which has proper caching.
  // SessionProvider manages session state in React context, which is sufficient.

  /**
   * Periodic session health check to ensure token is still valid
   */
  useEffect(() => {
    if (!session || process.env.NODE_ENV === 'test') return // Disable in tests

    const healthCheckInterval = setInterval(() => {
      // Check if current session is still valid
      if (!isSessionValid(session)) {
        logger.debug('Session health check failed, refreshing session')
        void updateSession()
      }
    }, 60000) // Check every minute

    return () => clearInterval(healthCheckInterval)
  }, [session, updateSession])

  return (
    <Context.Provider
      value={{
        session,
        error: isFailed,
        active: isActive,
        loading: updateInProgressRef.current,
        update: updateSession,
      }}
    >
      {children}
    </Context.Provider>
  )
}

interface ApolloSessionProviderProps {
  children: ReactNode
  pageProps: AppProps['pageProps']
  client?: ApolloClient<object>
}

const _client = createClient()

export const ApolloSessionProvider = ({
  children,
  pageProps,
  client = _client,
}: ApolloSessionProviderProps) => {
  const initialState = useMemo(() => {
    return pageProps['__APOLLO_STATE__'] || {}
  }, [pageProps])

  // hydrate apollo cache from SSR state
  useEffect(() => {
    if (initialState && client) {
      const existingCache = client.extract()
      const newCacheObject = merge(existingCache, initialState)
      client.cache.restore(newCacheObject)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialState])

  return <ApolloProvider client={client}>{children}</ApolloProvider>
}
